import { useEffect } from 'react';
import { AnnotationIcon } from '@heroicons/react/outline';
import { useSetAtom, useAtom } from 'jotai';
import { notificationAtom, pageLoadingAtom } from '../../atoms/system';
import {
  listLoadQuestionAtom,
  createQuestionAtom,
  editQuestionAtom,
} from '../../atoms/question';
import { popupReplyAtom } from '../../atoms/reply';
import api from '../../api';
import _ from 'lodash';
import cuid from 'cuid';
import {
  IResponse,
  CommentStatus,
  SearchProduct,
  SortBy,
  TargetItem,
  ActionType,
} from '../../types';
import {
  Header,
  ProductSearch as FilterProductSearch,
  DeleteConfirm as ListQuestionDeleteConfirm,
} from '../../widgets';
import ReactPaginate from 'react-paginate';
import { useImmer } from 'use-immer';
import queryString from 'query-string';
import ItemQuestion from './Item.Question';
import CreateQuestion from './Create.Question';
import EditQuestion from './Edit.Question';
import PopupQuestionReply from '../Reply/Popup.Reply';
import { LISTURL } from '../../config';
import produce from 'immer';
import clsx from 'clsx';
import { IQuestion } from '../../inter/list';
import { ListQuestionPayload } from '../../schema';

const breadcrumbs = [
  { name: 'Câu hỏi', href: LISTURL.question_list, current: false },
  { name: 'Danh sách', href: LISTURL.question_list, current: true },
];

const Action = (): JSX.Element => {
  const create = useSetAtom(createQuestionAtom);
  return (
    <button
      type="button"
      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      onClick={() => {
        create(true);
      }}
    >
      Tạo câu hỏi mới
    </button>
  );
};

const List = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const [listLoad, setListLoad] = useAtom(listLoadQuestionAtom);
  const [listData, setListData] = useImmer<IQuestion>({
    limit: 50,
    list: [],
    page: 1,
    total: -1,
  });
  const [listTarget, setListTarget] = useImmer<{ [key: string]: TargetItem }>(
    {},
  );
  const [isFiltered, setIsFiltered] = useImmer<boolean>(false);
  const [queryOptions, setQueryOptions] = useImmer<ListQuestionPayload>({
    page: listData.page,
    limit: listData.limit,
    sort_by: SortBy.createdDesc,
    target_id: null,
    status: null,
  });

  const [productSearch, setProductSearch] = useImmer<{
    show: boolean;
    item: SearchProduct;
  }>({
    show: false,
    item: {
      title: '',
      product_id: '',
      handle: '',
    },
  });
  useEffect(() => {
    if (productSearch.item.product_id.length > 0) {
      setQueryOptions(draft => {
        draft.target_id = productSearch.item.product_id;
      });
    }
  }, [productSearch.item.product_id]);

  useEffect(() => {
    (async () => {
      try {
        pageLoading(true);
        const query = queryString.stringify(queryOptions, {
          skipNull: true,
          skipEmptyString: true,
        });
        const result = await api
          .get(`api/questions?${query}`)
          .json<IResponse<IQuestion>>();
        const targetItems: { [key: string]: TargetItem } = {};
        if (result.data.list.length > 0) {
          const targetIds = _.map(result.data.list, v => v.pageTargetId);
          const targetSearch = await api
            .post('api/search/product', {
              json: {
                q: JSON.stringify(targetIds),
              },
            })
            .json<IResponse<TargetItem[]>>();
          _.forEach(targetSearch.data, v => {
            targetItems[v.product_id] = v;
          });
        }
        setListTarget(targetItems);
        setListData(result.data);
      } catch (e) {
        console.log(e);
        notification({
          show: true,
          type: null,
          title: null,
        });
      } finally {
        pageLoading(false);
      }
    })();
  }, [listLoad]);

  const setShowPopupRely = useSetAtom(popupReplyAtom);
  const setShowEditQuestion = useSetAtom(editQuestionAtom);
  const [showDeleteItem, setShowDeleteItem] = useImmer<{
    show: boolean;
    comment_id: string;
  }>({
    show: false,
    comment_id: '',
  });
  const handleDeleteItem = async (comment_id: string) => {
    try {
      pageLoading(true);
      await api.post(`api/comments/${comment_id}/delete-question`);
      setShowDeleteItem(draft => {
        draft.show = false;
        draft.comment_id = '';
      });
      setListLoad(cuid());
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  const handleAction = async (
    type: ActionType,
    item: { id: string; target?: Omit<TargetItem, '_id' | 'product_id'> },
  ) => {
    if (type === ActionType.reply) {
      setShowPopupRely(
        produce(draft => {
          draft.show = true;
          draft.reply_id = null;
          draft.parent_id = item.id;
          if (!_.isUndefined(item.target)) {
            draft.target.title = item.target.title;
            draft.target.handle = item.target.handle;
          }
        }),
      );
    } else if (type === ActionType.edit) {
      setShowEditQuestion(
        produce(draft => {
          draft.show = true;
          draft.comment_id = item.id;
          if (!_.isUndefined(item.target)) {
            draft.target_title = item.target.title;
          }
        }),
      );
    } else if (type === ActionType.delete) {
      setShowDeleteItem(draft => {
        draft.show = true;
        draft.comment_id = item.id;
      });
    } else if (type === ActionType.approve) {
      try {
        pageLoading(true);
        await api.post(`api/comments/${item.id}/approve`);
        setListLoad(cuid());
      } catch (e) {
        console.log(e);
        notification({
          show: true,
          type: null,
          title: null,
        });
      } finally {
        pageLoading(false);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Header
          title="Danh sách câu hỏi"
          breadcrumbs={breadcrumbs}
          action={<Action />}
        />
        <div className="p-3 mt-3">
          <div className="flex flex-col space-y-4">
            <div className="whitespace-nowrap overflow-x-auto flex space-x-4">
              <div className="flex flex-1 basis-2/3">
                <input
                  className={clsx(
                    isFiltered ? 'opacity-75 pointer-events-none' : null,
                    'bg-white border-gray-200 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 appearance-none block w-full px-3 py-2 pr-10 border rounded-md shadow-xs focus:outline-none sm:text-sm cursor-pointer',
                  )}
                  placeholder="Lọc theo sản phẩm"
                  readOnly
                  value={productSearch.item.title}
                  onClick={event => {
                    event.preventDefault();
                    setProductSearch(draft => {
                      draft.show = true;
                    });
                  }}
                />
              </div>
              <div className="flex flex-1 basis-1/3 space-x-2">
                <select
                  onChange={(event: any) => {
                    setQueryOptions(draft => {
                      draft.sort_by = event.target.value;
                    });
                  }}
                  value={queryOptions.sort_by}
                  className={clsx(
                    isFiltered ? 'opacity-75 pointer-events-none' : null,
                    'flex-1 text-gray-700 bg-white hover:bg-gray-100 py-2 px-4 pr-10 border border-gray-200 shadow-xs outline-none rounded-md text-sm',
                  )}
                >
                  <option value={SortBy.createdDesc}>Mới nhất</option>
                  <option value={SortBy.createdAsc}>Củ nhất</option>
                </select>
                {isFiltered ? (
                  <button
                    type="button"
                    onClick={() => {
                      setQueryOptions(draft => {
                        draft.page = 1;
                        draft.target_id = null;
                        draft.status = null;
                        draft.sort_by = SortBy.createdDesc;
                      });
                      setProductSearch(draft => {
                        draft.item.product_id = '';
                        draft.item.title = '';
                        draft.item.handle = '';
                      });
                      setListLoad(cuid());
                      setIsFiltered(false);
                    }}
                    className="whitespace-nowrap flex-1 text-white bg-yellow-600 hover:bg-yellow-700 py-2 px-4 shadow-xs outline-none rounded-md text-sm font-medium"
                  >
                    Xoá bộ lọc
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setQueryOptions(draft => {
                        draft.page = 1;
                        draft.status = null;
                      });
                      setListLoad(cuid());
                      setIsFiltered(true);
                    }}
                    className="max-w-[200px] whitespace-nowrap flex-1 text-white bg-indigo-600 hover:bg-indigo-700 py-2 px-4 border shadow-xs outline-none rounded-md text-sm font-medium"
                  >
                    Áp dụng
                  </button>
                )}
              </div>
            </div>
            <div className="whitespace-nowrap overflow-x-auto flex space-x-8 text-md text-gray-600 font-medium">
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = null;
                  });
                  setListLoad(cuid());
                  setIsFiltered(true);
                }}
                className={clsx(
                  queryOptions.status === null
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Tất cả câu hỏi
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.pending;
                  });
                  setListLoad(cuid());
                  setIsFiltered(true);
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.pending
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Câu hỏi chờ duyệt
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.active;
                  });
                  setListLoad(cuid());
                  setIsFiltered(true);
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.active
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Câu hỏi đã duyệt
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.inactive;
                  });
                  setListLoad(cuid());
                  setIsFiltered(true);
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.inactive
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Câu hỏi tạm ẩn
              </button>
            </div>
            {listData.total < 0 ? null : listData.total > 0 ? (
              <div className="flex flex-col space-y-2">
                <div className="overflow-x-auto whitespace-nowrap">
                  <div className=" min-w-[1050px] flex justify-between text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <div className="px-0 py-4 text-left w-[400px] sm:w-[400px] lg:w-[500px] xl:w-[600px] 2xl:w-[800px]">
                      Câu hỏi
                    </div>
                    <div className="w-[100px] px-2 py-4">Trạng thái</div>
                    <div className="w-[80px] px-2 py-4">Thích</div>
                    <div className="w-[80px] px-2 py-4">Trả lời</div>
                    <div className="min-w-[180x] flex-1 px-2 py-4"></div>
                  </div>
                  <div className="min-w-[1050px] flex flex-col space-y-3 whitespace-normal">
                    {listData.list.map((item, key: number) => (
                      <ItemQuestion
                        props={item}
                        target={listTarget[item.pageTargetId]}
                        action={handleAction}
                        key={key}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center flex-col-reverse lg:flex-row">
                  <div className="flex-1 basis-1/6 justify-start text-xs text-gray-500 hidden lg:flex"></div>
                  <div className="flex flex-1 basis-4/6 justify-center">
                    <ReactPaginate
                      pageCount={_.ceil(listData.total / listData.limit)}
                      containerClassName="flex justify-center space-x-2 mt-2"
                      pageLinkClassName="bg-white text-gray-500 hover:bg-gray-50 w-8 h-8 inline-flex justify-center items-center text-xs shadow-xs rounded-full"
                      activeLinkClassName="!bg-indigo-600 !text-gray-50 pointer-events-none"
                      breakLinkClassName="pointer-events-none bg-white text-gray-500 hover:bg-gray-50 w-8 h-8 inline-flex justify-center items-center text-xs shadow-xs rounded-full"
                      nextClassName="hidden"
                      previousClassName="hidden"
                      forcePage={listData.page - 1}
                      onPageChange={page => {
                        setQueryOptions(draft => {
                          draft.page = page.selected + 1;
                        });
                        setListLoad(cuid());
                      }}
                    />
                  </div>
                  <div className="flex flex-1 basis-1/6 justify-end text-xs text-gray-500">
                    Tổng {listData.total} câu hỏi
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col py-12">
                <div className="text-center">
                  <AnnotationIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-md font-medium text-gray-900">
                    Chưa có câu hỏi nào
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Tạo ngay câu hỏi mới để bắt đầu
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <CreateQuestion />
      <EditQuestion />
      <PopupQuestionReply page_type="question" />
      <FilterProductSearch
        show={productSearch.show}
        action={setProductSearch}
      />
      <ListQuestionDeleteConfirm
        payload={showDeleteItem}
        modal={setShowDeleteItem}
        action={handleDeleteItem}
      />
    </>
  );
};

export default List;
